// Ansicht Historie (Issue #82).
//
// Zeigt die Projekt-Historie als Commit-Zeitleiste (neueste zuerst): je Commit Revision,
// Zeitstempel, Autor und die geänderten Objekte als Badges. Ein Klick auf eine Zeile klappt sie
// auf und lädt LAZY den Diff der geänderten Objekte (getHistoryDiff) — als einfaches,
// zeilen-gefärbtes <pre> (keine Diff-Lib). „Zu diesem Stand zurück" ruft nach Bestätigung
// revertProject; der Knopf erscheint nur, wenn der angemeldete Nutzer das Recht „editieren" trägt
// (aus GET /me abgeleitet). Nach erfolgreichem Revert stößt bumpReload das Neuladen der aktiven
// Views an. Paginierung „Mehr laden" über nextBefore.

import { useCallback, useEffect, useState, type JSX } from 'react';
import { useI18n } from '../i18n';
import { useStore } from '../state/store';
import { getHistory, getHistoryDiff, getMe, hasEditRight, revertProject } from '../api/client';
import type { HistoryCommit, HistoryResponse } from '../api/types';
import { Button, EmptyState, PanelHeading } from '../components/ui';

const PAGE_SIZE = 50;

// Färbt eine Diff-Zeile ein: + grün, - rot, @@-Hunk gedämpft, Rest neutral. Bewusst schlicht
// (kein Wort-Diff), das genügt für die Objekt-JSON-Diffs des Backends.
function diffLineClass(line: string): string {
  if (line.startsWith('+') && !line.startsWith('+++')) return 'text-emerald-300';
  if (line.startsWith('-') && !line.startsWith('---')) return 'text-red-300';
  if (line.startsWith('@@')) return 'text-bf-gold';
  if (line.startsWith('diff ') || line.startsWith('index ') || line.startsWith('+++') || line.startsWith('---'))
    return 'text-bf-muted';
  return 'text-bf-white';
}

function DiffBlock({ text }: { text: string }): JSX.Element {
  const lines = text.split('\n');
  return (
    <pre
      data-testid="history-diff-pre"
      className="mt-2 max-h-80 overflow-auto rounded border border-bf-border bg-bf-navy p-2 text-xs leading-relaxed"
    >
      {lines.map((line, i) => (
        <div key={i} className={diffLineClass(line)}>
          {line || ' '}
        </div>
      ))}
    </pre>
  );
}

export function HistoryView(): JSX.Element {
  const { t } = useI18n();
  const { activeProjectId, notify, reportError, bumpReload } = useStore();

  const [page, setPage] = useState<HistoryResponse | null>(null);
  const [commits, setCommits] = useState<HistoryCommit[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [diffs, setDiffs] = useState<Record<string, string>>({});
  const [diffBusy, setDiffBusy] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [reverting, setReverting] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Recht „editieren" aus dem angemeldeten Principal ableiten (GET /me). Schlägt der Aufruf fehl
  // (z. B. statisches Token ohne Store-Nutzer), bleibt der Revert-Knopf verborgen — das Backend
  // sichert die Aktion ohnehin mit 403 ab.
  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((me) => !cancelled && setCanEdit(hasEditRight(me.roles)))
      .catch(() => !cancelled && setCanEdit(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Erste Seite laden (bei Projektwechsel Zustand zurücksetzen).
  const loadFirst = useCallback(
    async (projectId: string) => {
      setLoading(true);
      try {
        const res = await getHistory(projectId, { limit: PAGE_SIZE });
        setPage(res);
        setCommits(res.commits);
        setExpanded(null);
        setDiffs({});
      } catch (err) {
        reportError(err, t('history.loadFailed'));
      } finally {
        setLoading(false);
      }
    },
    [reportError, t],
  );

  useEffect(() => {
    if (!activeProjectId) {
      setPage(null);
      setCommits([]);
      return;
    }
    void loadFirst(activeProjectId);
  }, [activeProjectId, loadFirst]);

  // Nächste (ältere) Seite anhängen.
  const loadMore = useCallback(async () => {
    if (!activeProjectId || !page?.nextBefore) return;
    setLoading(true);
    try {
      const res = await getHistory(activeProjectId, { limit: PAGE_SIZE, before: page.nextBefore });
      setPage(res);
      setCommits((prev) => [...prev, ...res.commits]);
    } catch (err) {
      reportError(err, t('history.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [activeProjectId, page, reportError, t]);

  // Zeile auf-/zuklappen; beim Aufklappen die Diffs der geänderten Objekte lazy nachladen.
  const toggle = useCallback(
    async (commit: HistoryCommit) => {
      if (expanded === commit.rev) {
        setExpanded(null);
        return;
      }
      setExpanded(commit.rev);
      if (!activeProjectId) return;
      // Je geändertem Objekt genau einmal laden (Schlüssel rev + Objekttyp).
      for (const obj of commit.objects) {
        const key = `${commit.rev}:${obj.object}`;
        if (diffs[key] !== undefined) continue;
        setDiffBusy(key);
        try {
          const res = await getHistoryDiff(activeProjectId, commit.rev, obj.object);
          setDiffs((prev) => ({ ...prev, [key]: res.diff }));
        } catch (err) {
          reportError(err, t('history.diffFailed'));
          setDiffs((prev) => ({ ...prev, [key]: '' }));
        } finally {
          setDiffBusy(null);
        }
      }
    },
    [activeProjectId, expanded, diffs, reportError, t],
  );

  // Nicht-destruktiver Rollback nach Bestätigung; danach die aktiven Views neu laden + Historie
  // frisch ziehen (der Revert ist selbst ein neuer Commit oben in der Liste).
  const revert = useCallback(
    async (commit: HistoryCommit) => {
      if (!activeProjectId) return;
      if (!window.confirm(t('history.revertConfirm', { rev: commit.rev }))) return;
      setReverting(commit.rev);
      try {
        const res = await revertProject(activeProjectId, commit.fullRev);
        notify('success', t('history.reverted', { rev: commit.rev, newRev: res.rev }));
        bumpReload();
        await loadFirst(activeProjectId);
      } catch (err) {
        reportError(err, t('history.revertFailed'));
      } finally {
        setReverting(null);
      }
    },
    [activeProjectId, notify, reportError, bumpReload, loadFirst, t],
  );

  if (!activeProjectId) {
    return (
      <div className="h-full p-6">
        <EmptyState title={t('history.empty')} hint={t('history.emptyHint')} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-auto p-6">
      <div className="mb-4">
        <PanelHeading>{t('history.title')}</PanelHeading>
        <p className="max-w-3xl text-xs text-bf-muted">{t('history.hint')}</p>
      </div>

      {commits.length === 0 && !loading ? (
        <EmptyState title={t('history.noCommits')} />
      ) : (
        <ol className="flex flex-col gap-2">
          {commits.map((commit) => {
            const open = expanded === commit.rev;
            return (
              <li
                key={commit.rev}
                data-testid={`history-commit-${commit.rev}`}
                className="rounded border border-bf-border bg-bf-navy-light"
              >
                <button
                  type="button"
                  onClick={() => void toggle(commit)}
                  aria-expanded={open}
                  className="flex w-full items-start justify-between gap-4 px-3 py-2 text-left hover:bg-bf-navy"
                >
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-baseline gap-2">
                      <code className="rounded bg-bf-navy px-1.5 py-0.5 text-xs text-bf-gold">
                        {commit.rev}
                      </code>
                      <span className="truncate text-sm text-bf-white">{commit.summary}</span>
                    </span>
                    <span className="mt-1 block text-xs text-bf-muted">
                      {commit.ts} · {t('history.author')}: {commit.author}
                    </span>
                    <span className="mt-1 flex flex-wrap gap-1">
                      {commit.objects.map((obj) => (
                        <span
                          key={`${obj.object}:${obj.file}`}
                          data-testid={`history-badge-${commit.rev}-${obj.object}`}
                          className="rounded border border-bf-border px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-bf-muted"
                          title={`${obj.file} (${obj.change})`}
                        >
                          {obj.object}
                        </span>
                      ))}
                    </span>
                  </span>
                </button>

                {open && (
                  <div className="border-t border-bf-border px-3 py-2">
                    {canEdit && (
                      <div className="mb-2">
                        <Button
                          variant="danger"
                          disabled={reverting === commit.rev}
                          onClick={() => void revert(commit)}
                          testId={`history-revert-${commit.rev}`}
                        >
                          {reverting === commit.rev ? t('history.reverting') : t('history.revert')}
                        </Button>
                      </div>
                    )}
                    {commit.objects.map((obj) => {
                      const key = `${commit.rev}:${obj.object}`;
                      const diff = diffs[key];
                      return (
                        <div key={key} className="mt-2">
                          <span className="text-xs font-semibold text-bf-muted">
                            {obj.object} · {obj.file}
                          </span>
                          {diffBusy === key && diff === undefined ? (
                            <p className="mt-1 text-xs text-bf-muted">{t('common.loading')}</p>
                          ) : diff ? (
                            <DiffBlock text={diff} />
                          ) : (
                            <p className="mt-1 text-xs text-bf-muted">{t('history.diffEmpty')}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {page?.hasMore && (
        <div className="mt-4">
          <Button onClick={() => void loadMore()} disabled={loading} testId="history-load-more">
            {t('history.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
